import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Sparkles, Image as ImageIcon, MessageSquare, Phone, Download,
  TrendingUp, Activity, Crown, ArrowLeft, RefreshCw, Video,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

const Admin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [subs, setSubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get('/admin/overview'),
        api.get('/admin/whatsapp-subs'),
        api.get('/admin/users'),
      ]);
      setOverview(a.data);
      setSubs(b.data.subscribers || []);
      setUsers(c.data.users || []);
    } catch (err) {
      toast({ title: 'Could not load admin data', description: err?.response?.data?.detail || 'You may not have permission.' });
    } finally {
      setLoading(false);
    }
  };

  const exportUsersCSV = () => {
    const header = 'Name,Email,BusinessName,Plan,SubscriptionStatus,Provider,ConsentAt,ContractsCount,StripeCustomerId,Joined\n';
    const rows = users.map(u => [
      `"${(u.name || '').replace(/"/g, '""')}"`,
      `"${u.email || ''}"`,
      `"${(u.businessName || '').replace(/"/g, '""')}"`,
      `"${u.plan || ''}"`,
      `"${u.subscriptionStatus || ''}"`,
      `"${u.provider || ''}"`,
      `"${u.consent?.agreedAt || ''}"`,
      `"${u.contractsCount ?? 0}"`,
      `"${u.stripeCustomerId || ''}"`,
      `"${u.created_at || ''}"`,
    ].join(',')).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mbap-users-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => { load(); }, []);

  if (!user) return null;
  if (!user.isAdmin) {
    return (
      <div className="min-h-screen bg-[#fdfaf6]">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 py-20 text-center">
          <Crown className="w-12 h-12 mx-auto text-slate-300" />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Admin only</h1>
          <p className="mt-1 text-slate-500">You don’t have access to this page.</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-6">Back to dashboard</Button>
        </div>
      </div>
    );
  }

  const exportSubsCSV = () => {
    const header = 'Name,Phone,BusinessName,BusinessType,SubscribedAt\n';
    const rows = subs.map(s => [
      `"${(s.name || '').replace(/"/g, '""')}"`,
      `"${s.phone || ''}"`,
      `"${(s.businessName || '').replace(/"/g, '""')}"`,
      `"${s.businessType || ''}"`,
      `"${s.subscribedAt || ''}"`,
    ].join(',')).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whatsapp-subscribers-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = overview?.totals || {};
  const last14 = overview?.signupsLast14Days || [];
  const maxSignup = Math.max(1, ...last14.map(d => d.count));
  const funnel = overview?.funnel || [];
  const maxFunnel = Math.max(1, ...funnel.map(f => f.count));
  const planDist = overview?.planDistribution || {};
  const planEntries = Object.entries(planDist);
  const totalPlans = planEntries.reduce((s, [, n]) => s + n, 0) || 1;

  const statCards = [
    { label: 'Total signups', value: totals.users || 0, icon: Users, bg: 'bg-blue-100', text: 'text-blue-600' },
    { label: 'Ads generated', value: totals.adsGenerated || 0, icon: Sparkles, bg: 'bg-orange-100', text: 'text-orange-600' },
    { label: 'Images generated', value: totals.imagesGenerated || 0, icon: ImageIcon, bg: 'bg-rose-100', text: 'text-rose-600' },
    { label: 'Reviews answered', value: totals.reviewsResponded || 0, icon: MessageSquare, bg: 'bg-emerald-100', text: 'text-emerald-600' },
    { label: 'Saved ads', value: totals.savedAds || 0, icon: Activity, bg: 'bg-amber-100', text: 'text-amber-600' },
    { label: 'WhatsApp leads', value: totals.whatsappSubs || 0, icon: Phone, bg: 'bg-violet-100', text: 'text-violet-600' },
  ];

  return (
    <div className="min-h-screen bg-[#fdfaf6]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">Founder Dashboard</h1>
              <span className="text-xs bg-gradient-to-r from-orange-500 to-rose-500 text-white font-bold px-2.5 py-1 rounded-full uppercase">Admin</span>
            </div>
            <p className="mt-1 text-slate-600">Hello {user.name?.split(' ')[0] || 'Chirag'} — here’s how MyBusinessAdPro is doing.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/video')} data-testid="goto-video-studio" className="border-orange-300 text-orange-700 hover:bg-orange-50">
              <Video className="w-4 h-4 mr-1.5" /> AI Video Studio
            </Button>
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Stat grid */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {statCards.map(s => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-4 h-4 ${s.text}`} />
              </div>
              <div className="mt-3 text-2xl font-extrabold text-slate-900">{s.value}</div>
              <div className="text-xs text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="insights" className="mt-8">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="insights">Insights</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="whatsapp">WhatsApp Leads ({subs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="insights" className="mt-5">
            <div className="grid lg:grid-cols-2 gap-5">
              {/* Signups chart */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <div className="font-bold text-slate-900">Signups (last 14 days)</div>
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                </div>
                <div className="mt-5 flex items-end gap-1.5 h-44">
                  {last14.map(d => (
                    <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1">
                      <div
                        className="w-full rounded-t bg-gradient-to-t from-orange-400 to-rose-400 hover:opacity-80 transition-opacity"
                        style={{ height: `${(d.count / maxSignup) * 100}%`, minHeight: d.count > 0 ? '4px' : '2px' }}
                        title={`${d.count} on ${d.date}`}
                      />
                      <div className="text-[9px] text-slate-400">{d.date.slice(8,10)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Funnel */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="font-bold text-slate-900">Conversion funnel</div>
                <div className="mt-5 space-y-3">
                  {funnel.map(f => (
                    <div key={f.stage}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">{f.stage}</span>
                        <span className="font-semibold text-slate-900">{f.count}</span>
                      </div>
                      <div className="mt-1.5 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-orange-400 to-rose-500"
                          style={{ width: `${(f.count / maxFunnel) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Plan distribution */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="font-bold text-slate-900">Plan distribution</div>
                <div className="mt-5 space-y-3">
                  {planEntries.length === 0 && <div className="text-sm text-slate-400">No data yet</div>}
                  {planEntries.map(([plan, n]) => (
                    <div key={plan}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="capitalize text-slate-700">{plan}</span>
                        <span className="text-slate-500">{n} ({Math.round(n / totalPlans * 100)}%)</span>
                      </div>
                      <div className="mt-1.5 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            plan === 'agency' ? 'bg-violet-500' : plan === 'pro' ? 'bg-orange-500' : 'bg-slate-400'
                          }`}
                          style={{ width: `${(n / totalPlans) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top users */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="font-bold text-slate-900">Top users by activity</div>
                <div className="mt-3 divide-y divide-slate-100">
                  {(overview?.topUsers || []).length === 0 && <div className="text-sm text-slate-400 py-2">No activity yet</div>}
                  {(overview?.topUsers || []).map((u, i) => (
                    <div key={u.userId} className="flex items-center gap-3 py-2.5">
                      <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-700">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">{u.name}</div>
                        <div className="text-xs text-slate-500 truncate">{u.email}</div>
                      </div>
                      <div className="text-xs text-slate-500 capitalize bg-slate-50 px-2 py-0.5 rounded-full">{u.plan}</div>
                      <div className="text-sm font-bold text-slate-900 w-10 text-right">{u.events}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-slate-600">
                Showing <strong>{users.length}</strong> account{users.length === 1 ? '' : 's'}.
                {' '}Real customers will have a <span className="font-semibold">SubscriptionStatus</span> of <code className="text-xs bg-slate-100 px-1 rounded">trialing</code>, <code className="text-xs bg-slate-100 px-1 rounded">active</code> or <code className="text-xs bg-slate-100 px-1 rounded">past_due</code>.
              </div>
              <Button onClick={exportUsersCSV} variant="outline" size="sm" data-testid="export-users-csv">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
              </Button>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[920px]" data-testid="admin-users-table">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Business</th>
                    <th className="text-left px-4 py-3">Plan</th>
                    <th className="text-left px-4 py-3">Subscription</th>
                    <th className="text-left px-4 py-3">Contract</th>
                    <th className="text-left px-4 py-3">Consent</th>
                    <th className="text-left px-4 py-3">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.length === 0 && (
                    <tr><td colSpan="8" className="text-center py-8 text-slate-400">No users yet</td></tr>
                  )}
                  {users.map(u => {
                    const sub = u.subscriptionStatus;
                    const subColors = {
                      trialing: 'bg-blue-100 text-blue-700',
                      active: 'bg-emerald-100 text-emerald-700',
                      past_due: 'bg-amber-100 text-amber-700',
                      canceled: 'bg-slate-200 text-slate-600',
                    };
                    return (
                      <tr key={u.id}>
                        <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                        <td className="px-4 py-3 text-slate-600">{u.email}</td>
                        <td className="px-4 py-3 text-slate-600">{u.businessName || '—'}</td>
                        <td className="px-4 py-3"><span className="capitalize bg-slate-100 px-2 py-0.5 rounded-full text-xs">{u.plan}</span></td>
                        <td className="px-4 py-3">
                          {sub
                            ? <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${subColors[sub] || 'bg-slate-100 text-slate-600'}`}>{sub}</span>
                            : <span className="text-xs text-slate-400 italic">no payment</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {u.contractsCount > 0
                            ? <span className="text-emerald-600 font-medium">✓ Sent ({u.contractsCount})</span>
                            : <span className="text-slate-400">—</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {u.consent?.agreedAt
                            ? new Date(u.consent.agreedAt).toLocaleDateString('en-GB')
                            : <span className="text-slate-400">—</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{(u.created_at || '').slice(0,10)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="whatsapp" className="mt-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-slate-600">Lead capture from the landing page — ready for WhatsApp Business outreach.</p>
              <Button onClick={exportSubsCSV} disabled={!subs.length} variant="outline">
                <Download className="w-4 h-4 mr-1.5" /> Export CSV
              </Button>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Phone</th>
                    <th className="text-left px-4 py-3">Business</th>
                    <th className="text-left px-4 py-3">Type</th>
                    <th className="text-left px-4 py-3">Subscribed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {subs.length === 0 && (
                    <tr><td colSpan="5" className="text-center py-8 text-slate-400">No subscribers yet</td></tr>
                  )}
                  {subs.map(s => (
                    <tr key={s.id}>
                      <td className="px-4 py-3 font-medium text-slate-900">{s.name}</td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-xs">{s.phone}</td>
                      <td className="px-4 py-3 text-slate-600">{s.businessName || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 capitalize">{s.businessType || '—'}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{(s.subscribedAt || '').slice(0,10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
